import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Typography
} from '@mui/material';

/**
 * Part III / company-policy override dialog for Crew Duty Roster.
 * Hard DGCA blocks outside Part III cannot proceed from this dialog.
 */
const CrewDutyRosterOverrideDialog = ({
  overrideDialogOpen,
  setOverrideDialogOpen,
  formData,
  setFormData,
  aircraftType,
  complianceContext,
  complianceViolations,
  complianceWarnings,
  partIiiEligible,
  requiredCompensatoryRestHours,
  hardBlockMessages,
  handleOverride
}) => {
  const hasHardBlocks = (hardBlockMessages || []).length > 0 && !partIiiEligible;
  const canProceed =
    !hasHardBlocks &&
    formData.nonComplianceJustification &&
    formData.nonComplianceJustification.length >= 50 &&
    (!partIiiEligible ||
      !requiredCompensatoryRestHours ||
      Number(formData.compensatoryRestHours) >= requiredCompensatoryRestHours);

  return (
    <Dialog open={overrideDialogOpen} onClose={() => setOverrideDialogOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>
        {partIiiEligible ? 'Part III Extension Required' : hasHardBlocks ? 'DGCA Hard Block' : 'Compliance Justification Required'}
      </DialogTitle>
      <DialogContent>
        {complianceContext && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              <strong>Aircraft Type:</strong> {complianceContext.aircraftType}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Regulation:</strong> {complianceContext.regulationReference}
            </Typography>
            <Typography variant="body2">
              <strong>Compliance Source:</strong> {complianceContext.isBaseline
                ? 'Baseline Configuration (Default DGCA limits)'
                : `Active Profile: "${complianceContext.profileName}"`}
            </Typography>
          </Alert>
        )}
        {!complianceContext && aircraftType && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Aircraft Type:</strong> {aircraftType} — checks use{' '}
              {aircraftType === 'Fixed Wing' ? 'D7J-J4-Fixed Wing.pdf' : 'D7J-J2 - Helicopter.pdf'}
            </Typography>
          </Alert>
        )}

        {hasHardBlocks && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              These DGCA violations cannot be overridden (outside Part III):
            </Typography>
            <ul>
              {(hardBlockMessages || []).map((v, idx) => (
                <li key={idx}>{v}</li>
              ))}
            </ul>
          </Alert>
        )}

        {!hasHardBlocks && (
          <Alert severity={partIiiEligible ? 'warning' : 'error'} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {partIiiEligible
                ? 'Part III allows a limited FDP/flight-time extension with justification and compensatory rest.'
                : 'Violations:'}
            </Typography>
            <ul>
              {complianceViolations.map((v, idx) => (
                <li key={idx}>{typeof v === 'string' ? v : v.message || JSON.stringify(v)}</li>
              ))}
            </ul>
          </Alert>
        )}

        {complianceWarnings.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Warnings:</Typography>
            <ul>
              {complianceWarnings.map((w, idx) => (
                <li key={idx}>{w.message || w}</li>
              ))}
            </ul>
          </Alert>
        )}

        {!hasHardBlocks && (
          <>
            {partIiiEligible && requiredCompensatoryRestHours > 0 && (
              <TextField
                fullWidth
                type="number"
                label={`Compensatory Rest Hours (min ${requiredCompensatoryRestHours})`}
                value={formData.compensatoryRestHours}
                onChange={(e) => setFormData({ ...formData, compensatoryRestHours: e.target.value })}
                required
                helperText="DGCA Part III: +1h extension → +2h rest; beyond 1h (up to 2h) → +4h rest"
                sx={{ mt: 1 }}
              />
            )}
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Justification (Required, min 50 characters)"
              value={formData.nonComplianceJustification}
              onChange={(e) => setFormData({ ...formData, nonComplianceJustification: e.target.value })}
              required
              helperText={`${(formData.nonComplianceJustification || '').length}/50 characters minimum`}
              sx={{ mt: 2 }}
            />
            <Alert severity="info" sx={{ mt: 2 }}>
              ACCOUNTABLE_MANAGER will be notified of this {partIiiEligible ? 'Part III extension' : 'override'}.
            </Alert>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOverrideDialogOpen(false)}>
          {hasHardBlocks ? 'Close' : 'Cancel'}
        </Button>
        {!hasHardBlocks && (
          <Button
            onClick={handleOverride}
            variant="contained"
            disabled={!canProceed}
          >
            {partIiiEligible ? 'Proceed with Part III Extension' : 'Proceed with Justification'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CrewDutyRosterOverrideDialog;
